import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGameStore, Order } from '../../store/gameStore';
import { getMenuByPeriod, getMealPeriodEmoji, getComplexityColor, MenuItem, INGREDIENTS } from '../../data/menuData';
import { NPC_CUSTOMERS, getCharacterSprite, getRandomNPC } from '../../data/npcData';

interface Props {
  send: (action: string, data?: object) => void;
}

type CookingPhase = 'queue' | 'ingredients' | 'cooking' | 'served';

export function ChefView({ send }: Props) {
  const { currentMealPeriod, orders, playerId, players, addNPCOrder } = useGameStore();
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [cookingStep, setCookingStep] = useState(0);
  const [phase, setPhase] = useState<CookingPhase>('queue');
  const [timingPosition, setTimingPosition] = useState(0);
  const [timingResult, setTimingResult] = useState<'pending' | 'perfect' | 'good' | 'ok'>('pending');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [completedOrder, setCompletedOrder] = useState<{item: MenuItem, customerName: string, cookingQuality: number} | null>(null);
  const [timingScores, setTimingScores] = useState<number[]>([]); // Track each step's score
  
  const npcIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const orderCountRef = useRef(0);
  const timingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const mealPeriod = currentMealPeriod || 'breakfast';
  const menu = getMenuByPeriod(mealPeriod);
  
  // Check if there are any human customers
  const humanCustomers = players.filter(p => p.role === 'customer');
  const humanChefs = players.filter(p => p.role === 'chef');
  const needsNPCCustomers = humanCustomers.length === 0;
  
  // Get the active order object
  const activeOrder = orders.find(o => o.id === activeOrderId);
  const activeMenuItem = activeOrder ? menu.find(m => m.id === activeOrder.menuItemId) : null;
  
  // Spawn NPC order function
  const spawnNPCOrder = useCallback(() => {
    if (!needsNPCCustomers) return;
    
    const pendingCount = orders.filter(o => o.status === 'pending').length;
    if (pendingCount >= 3) return;
    
    const npc = getRandomNPC();
    const randomItem = menu[Math.floor(Math.random() * menu.length)];
    
    orderCountRef.current += 1;
    const newOrder: Order = {
      id: `npc-order-${Date.now()}-${orderCountRef.current}`,
      customerId: npc.id,
      customerName: npc.name,
      menuItemId: randomItem.id,
      status: 'pending',
      createdAt: Date.now(),
    };
    
    addNPCOrder(newOrder);
  }, [needsNPCCustomers, orders, menu, addNPCOrder]);
  
  // Auto-spawn NPC orders if no human customers
  useEffect(() => {
    if (!needsNPCCustomers) {
      if (npcIntervalRef.current) {
        clearInterval(npcIntervalRef.current);
        npcIntervalRef.current = null;
      }
      return;
    }
    
    setTimeout(spawnNPCOrder, 1000);
    
    npcIntervalRef.current = setInterval(() => {
      spawnNPCOrder();
    }, 8000 + Math.random() * 7000);
    
    return () => {
      if (npcIntervalRef.current) {
        clearInterval(npcIntervalRef.current);
      }
    };
  }, [needsNPCCustomers, spawnNPCOrder]);
  
  // Timing bar animation - gets faster with each step!
  useEffect(() => {
    if (phase === 'cooking' && timingResult === 'pending') {
      // Speed increases with each step (step 1 = 4, step 2 = 5, step 3 = 6, etc.)
      const speed = 3 + cookingStep;
      // Update interval also gets faster
      const interval = Math.max(15, 25 - cookingStep * 3);
      
      timingIntervalRef.current = setInterval(() => {
        setTimingPosition(prev => {
          const next = prev + speed;
          return next > 100 ? 0 : next;
        });
      }, interval);
      
      return () => {
        if (timingIntervalRef.current) {
          clearInterval(timingIntervalRef.current);
        }
      };
    }
  }, [phase, timingResult, cookingStep]);
  
  // Elapsed time counter during cooking
  useEffect(() => {
    if (phase === 'ingredients' || phase === 'cooking') {
      setElapsedTime(0);
      elapsedIntervalRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      
      return () => {
        if (elapsedIntervalRef.current) {
          clearInterval(elapsedIntervalRef.current);
        }
      };
    }
  }, [phase]);
  
  // Get pending orders
  const pendingOrders = orders.filter(o => o.status === 'pending');

  const handleClaimOrder = (orderId: string) => {
    // For NPC orders, update locally. For real orders, send to backend
    const order = orders.find(o => o.id === orderId);
    if (order?.id.startsWith('npc-')) {
      // Update local state for NPC order
      useGameStore.setState(state => ({
        orders: state.orders.map(o => 
          o.id === orderId ? { ...o, status: 'cooking' as const, chefId: playerId || '' } : o
        )
      }));
    } else {
      send('claimOrder', { orderId });
    }
    setActiveOrderId(orderId);
    setSelectedIngredients([]);
    setCookingStep(0);
    setTimingScores([]); // Reset timing scores for new order
    setPhase('ingredients');
    setElapsedTime(0);
  };

  const handleIngredientClick = (ingredientId: string) => {
    if (selectedIngredients.includes(ingredientId)) {
      setSelectedIngredients(prev => prev.filter(i => i !== ingredientId));
    } else {
      setSelectedIngredients(prev => [...prev, ingredientId]);
    }
  };

  const handleStartCooking = () => {
    setCookingStep(1);
    setPhase('cooking');
    setTimingPosition(0);
    setTimingResult('pending');
  };

  const handleTimingClick = () => {
    // Check if timing is in zone - MUCH harder now!
    // Perfect zone: 46-54% (only 8% window!)
    // Good zone: 38-62% (24% window)
    // OK: anything else
    let result: 'perfect' | 'good' | 'ok';
    let stepScore: number;
    if (timingPosition >= 46 && timingPosition <= 54) {
      result = 'perfect';
      stepScore = 100; // Perfect = 100 points
    } else if (timingPosition >= 38 && timingPosition <= 62) {
      result = 'good';
      stepScore = 70; // Good = 70 points
    } else {
      result = 'ok';
      stepScore = 40; // OK = 40 points
    }
    setTimingResult(result);
    
    // Add score to tracking array
    setTimingScores(prev => [...prev, stepScore]);
    
    // Move to next step after short delay
    setTimeout(() => {
      if (cookingStep < 3) {
        setCookingStep(prev => prev + 1);
        setTimingResult('pending');
        setTimingPosition(0);
      } else {
        // Pass accumulated scores to completion
        const avgScore = Math.round([...timingScores, stepScore].reduce((a, b) => a + b, 0) / ([...timingScores, stepScore].length));
        handleCompleteOrder(avgScore);
      }
    }, 800);
  };

  const handleCompleteOrder = (cookingQuality: number = 70) => {
    if (!activeOrderId || !activeMenuItem) return;
    
    const customerName = getCustomerName(activeOrder?.customerId || '');
    
    // For NPC orders, update locally with cooking quality. For real orders, send to backend
    if (activeOrderId.startsWith('npc-')) {
      useGameStore.setState(state => ({
        orders: state.orders.map(o => 
          o.id === activeOrderId ? { ...o, status: 'delivered' as const, cookingQuality } : o
        )
      }));
    } else {
      send('completeOrder', { orderId: activeOrderId, cookingQuality });
    }
    
    // Show served screen with cooking quality
    setCompletedOrder({ item: activeMenuItem, customerName, cookingQuality });
    setPhase('served');
  };

  const handleBackToQueue = () => {
    setActiveOrderId(null);
    setPhase('queue');
    setCompletedOrder(null);
    setTimingScores([]); // Reset timing scores for next order
    setCookingStep(0);
    setSelectedIngredients([]);
  };

  // Get customer name for an order
  const getCustomerName = (customerId: string) => {
    const npc = NPC_CUSTOMERS.find(n => n.id === customerId);
    if (npc) return npc.name;
    
    const player = players.find(p => p.playerId === customerId);
    return player?.playerName || 'Customer';
  };
  
  // Get character image for an order
  const getCustomerCharacterImage = (customerId: string) => {
    const npc = NPC_CUSTOMERS.find(n => n.id === customerId);
    if (npc) return getCharacterSprite(npc.characterId);
    
    const player = players.find(p => p.playerId === customerId);
    return getCharacterSprite(player?.characterId);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ========== SERVED SCREEN ==========
  if (phase === 'served' && completedOrder) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <div style={{
          background: '#e8f5e9',
          border: '4px solid #4caf50',
          padding: 40,
          marginBottom: 24,
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <h2 style={{ color: '#2e7d32', marginBottom: 16 }}>Order Served!</h2>
          <img 
            src={completedOrder.item.image}
            alt={completedOrder.item.name}
            style={{ width: 96, height: 96, imageRendering: 'pixelated', marginBottom: 16 }}
          />
          <h3 style={{ color: '#333', marginBottom: 8 }}>{completedOrder.item.name}</h3>
          <p style={{ color: '#666', marginBottom: 24 }}>
            Delivered to <strong>{completedOrder.customerName}</strong>
          </p>
          
          <div style={{
            background: 'white',
            padding: 16,
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-around',
          }}>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Cooking Time</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#4caf50' }}>{formatTime(elapsedTime)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Target Time</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#4682b4' }}>{completedOrder.item.prepTime}:00</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Cooking Quality</div>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: completedOrder.cookingQuality >= 90 ? '#4caf50' : 
                       completedOrder.cookingQuality >= 70 ? '#ff9800' : '#f44336' 
              }}>
                {completedOrder.cookingQuality}%
              </div>
              <div style={{ fontSize: 11, color: '#666' }}>
                {completedOrder.cookingQuality >= 90 ? '🌟 PERFECT!' : 
                 completedOrder.cookingQuality >= 70 ? '👍 Good' : '😬 Needs work'}
              </div>
            </div>
          </div>
          
          <button
            onClick={handleBackToQueue}
            style={{
              padding: '16px 48px',
              background: '#4caf50',
              color: 'white',
              border: 'none',
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            📋 NEXT ORDER
          </button>
        </div>
      </div>
    );
  }

  // ========== COOKING PHASE ==========
  if (phase === 'cooking' && activeMenuItem) {
    const steps = activeMenuItem.steps || ['Prep ingredients', 'Cook', 'Plate & serve'];
    const currentStepName = steps[cookingStep - 1] || 'Complete';
    
    return (
      <div>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9f9f9',
          border: '3px solid #333',
          padding: 16,
          marginBottom: 24,
        }}>
          <div>
            <strong>Current Order:</strong> {activeMenuItem.name} for {getCustomerName(activeOrder?.customerId || '')}
          </div>
          <div style={{ textAlign: 'right' }}>
            <strong style={{ color: '#d4694f' }}>Target: {activeMenuItem.prepTime}:00</strong><br/>
            <span style={{ fontSize: 12, color: '#666' }}>Elapsed: {formatTime(elapsedTime)}</span>
          </div>
        </div>
        
        {/* Main cooking area */}
        <div style={{ textAlign: 'center', padding: 24, background: 'var(--ltc-bg-card)', border: 'var(--ltc-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <img 
              src={activeMenuItem.image}
              alt={activeMenuItem.name}
              style={{ width: 80, height: 80, imageRendering: 'pixelated' }}
            />
            <div>
              <h3 style={{ color: '#8b4513', margin: 0 }}>Preparing: {activeMenuItem.name}</h3>
              <p style={{ color: '#666', margin: 0, fontSize: 14 }}>
                Complexity: {activeMenuItem.complexity} | {steps.length} Steps
              </p>
            </div>
          </div>
          
          {/* Progress bar */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Progress</span>
              <span>{Math.round((cookingStep / steps.length) * 100)}%</span>
            </div>
            <div style={{
              height: 30,
              background: '#ddd',
              borderRadius: 15,
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${(cookingStep / steps.length) * 100}%`,
                background: 'linear-gradient(to right, #4caf50, #8bc34a)',
                transition: 'width 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 600,
              }}>
                {cookingStep > 0 && `${cookingStep}/${steps.length}`}
              </div>
            </div>
          </div>
          
          {/* Using ingredients display */}
          <div style={{
            background: '#f9f9f9',
            border: '3px solid #333',
            padding: 16,
            marginBottom: 24,
          }}>
            <strong>Using Ingredients:</strong>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              {activeMenuItem.ingredients.map((ingId, idx) => {
                const ing = INGREDIENTS.find(i => i.id === ingId);
                const isUsed = idx < cookingStep;
                const isCurrent = idx === cookingStep - 1;
                return (
                  <span key={ingId} style={{
                    background: isUsed ? '#e8f5e9' : isCurrent ? '#fff8e1' : 'white',
                    border: `2px solid ${isUsed ? '#4caf50' : isCurrent ? '#ff9800' : '#333'}`,
                    padding: '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: isUsed ? 0.6 : 1,
                  }}>
                    <img src={ing?.image} alt={ing?.name} style={{ width: 20, height: 20, imageRendering: 'pixelated' }} />
                    {ing?.name} {isUsed && '✓'}
                  </span>
                );
              })}
            </div>
          </div>
          
          {/* Recipe steps */}
          <div style={{ marginBottom: 24, textAlign: 'left' }}>
            {steps.map((step, idx) => (
              <div key={idx} style={{
                padding: 12,
                background: idx < cookingStep ? '#e8f5e9' : idx === cookingStep ? '#fff8e1' : '#f5f5f5',
                borderLeft: `4px solid ${idx < cookingStep ? '#4caf50' : idx === cookingStep ? '#ff9800' : '#ddd'}`,
                marginBottom: 8,
              }}>
                <span style={{ fontWeight: idx === cookingStep ? 600 : 400 }}>
                  {idx < cookingStep ? '✓' : idx === cookingStep ? '→' : ''} Step {idx + 1}: {step}
                </span>
              </div>
            ))}
          </div>
          
          {/* Timing Challenge */}
          {cookingStep <= steps.length && (
            <div style={{
              background: 'white',
              border: '3px solid #4caf50',
              padding: 20,
              marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>
                🎯 TIMING CHALLENGE - {currentStepName}
              </div>
              <div style={{
                height: 40,
                background: 'linear-gradient(to right, #ff6b6b 0%, #ff6b6b 38%, #ffc107 38%, #ffc107 46%, #4caf50 46%, #4caf50 54%, #ffc107 54%, #ffc107 62%, #ff6b6b 62%, #ff6b6b 100%)',
                border: '3px solid #333',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Moving indicator */}
                <div style={{
                  position: 'absolute',
                  width: 4,
                  height: '100%',
                  background: '#333',
                  left: `${timingPosition}%`,
                  transition: 'left 0.03s linear',
                }} />
                
                {/* Result overlay */}
                {timingResult !== 'pending' && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: timingResult === 'perfect' ? 'rgba(76, 175, 80, 0.8)' : 
                               timingResult === 'good' ? 'rgba(255, 193, 7, 0.8)' : 
                               'rgba(255, 152, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 18,
                  }}>
                    {timingResult === 'perfect' ? '✨ PERFECT!' : 
                     timingResult === 'good' ? '👍 GOOD!' : '👌 OK'}
                  </div>
                )}
              </div>
              
              <div style={{ marginTop: 16 }}>
                <button
                  onClick={handleTimingClick}
                  disabled={timingResult !== 'pending'}
                  style={{
                    padding: '16px 64px',
                    background: timingResult !== 'pending' ? '#ccc' : '#ff9800',
                    color: 'white',
                    border: 'none',
                    fontSize: 20,
                    fontWeight: 600,
                    cursor: timingResult !== 'pending' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {cookingStep === steps.length ? '✅ FINISH!' : '👆 TAP!'}
                </button>
              </div>
              <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                Hit the green zone for PERFECT timing!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========== INGREDIENT SELECTION PHASE ==========
  if (phase === 'ingredients' && activeMenuItem) {
    const requiredIngredients = activeMenuItem.ingredients;
    const allSelected = requiredIngredients.every(ing => selectedIngredients.includes(ing));
    const wrongSelected = selectedIngredients.filter(s => !requiredIngredients.includes(s));

    return (
      <div>
        {/* Info banner */}
        <div style={{
          background: '#e8f5e9',
          padding: 16,
          border: '3px solid #2e7d32',
          marginBottom: 24,
        }}>
          <strong>💡 CHEF INGREDIENT SYSTEM:</strong> Select the correct ingredients for your recipe!
        </div>
        
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9f9f9',
          border: '3px solid #333',
          padding: 16,
          marginBottom: 24,
        }}>
          <div>
            <strong>Current Order:</strong> {activeMenuItem.name} for {getCustomerName(activeOrder?.customerId || '')}
          </div>
          <div style={{ textAlign: 'right' }}>
            <strong style={{ color: '#d4694f' }}>Target: {activeMenuItem.prepTime}:00</strong>
          </div>
        </div>
        
        {/* Split view */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>
          {/* Left: Recipe Panel */}
          <div style={{
            background: 'white',
            border: '3px solid #333',
            padding: 20,
          }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#8b4513', borderBottom: '2px solid #333', paddingBottom: 8 }}>
              📋 Recipe Card
            </h4>
            
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img 
                src={activeMenuItem.image}
                alt={activeMenuItem.name}
                style={{ width: 80, height: 80, imageRendering: 'pixelated' }}
              />
              <div style={{ fontWeight: 600, marginTop: 8, fontSize: 14 }}>{activeMenuItem.name}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Complexity: {activeMenuItem.complexity} | {activeMenuItem.steps?.length || 3} Steps
              </div>
            </div>
            
            <h5 style={{ margin: '16px 0 10px 0', fontWeight: 600 }}>Required Ingredients:</h5>
            <div style={{
              background: '#fffef0',
              border: '3px dashed #8b4513',
              padding: 12,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
            }}>
              {requiredIngredients.map(ingId => {
                const ing = INGREDIENTS.find(i => i.id === ingId);
                const isSelected = selectedIngredients.includes(ingId);
                return (
                  <span key={ingId} style={{
                    background: isSelected ? '#e8f5e9' : 'white',
                    border: `2px solid ${isSelected ? '#2e7d32' : '#333'}`,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    <img src={ing?.image} alt={ing?.name} style={{ width: 16, height: 16, imageRendering: 'pixelated' }} />
                    {ing?.name} {isSelected && '✓'}
                  </span>
                );
              })}
            </div>
            
            {wrongSelected.length > 0 && (
              <div style={{ background: '#ffebee', border: '2px solid #c62828', padding: 10, marginTop: 12, fontSize: 12 }}>
                ⚠️ <strong>Wrong ingredients:</strong> {wrongSelected.map(id => INGREDIENTS.find(i => i.id === id)?.name).join(', ')}
              </div>
            )}
            
            {!allSelected && (
              <div style={{ background: '#fff3e0', border: '2px solid #e65100', padding: 10, marginTop: 12, fontSize: 12 }}>
                ⚠️ <strong>Missing:</strong> Select all required ingredients!
              </div>
            )}
            
            <button
              onClick={handleStartCooking}
              disabled={!allSelected || wrongSelected.length > 0}
              style={{
                width: '100%',
                marginTop: 16,
                padding: 14,
                background: (allSelected && wrongSelected.length === 0) ? 'var(--ltc-accent)' : '#ccc',
                color: 'white',
                border: 'none',
                fontSize: 14,
                fontWeight: 600,
                cursor: (allSelected && wrongSelected.length === 0) ? 'pointer' : 'not-allowed',
              }}
            >
              {(allSelected && wrongSelected.length === 0) ? '🍳 START COOKING' : 'Select all ingredients'}
            </button>
          </div>
          
          {/* Right: Ingredient Grid */}
          <div>
            <h4 style={{ margin: '0 0 16px 0', color: '#8b4513', borderBottom: '2px solid #333', paddingBottom: 8 }}>
              🧺 Available Ingredients
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: 10,
              background: '#f5f5f5',
              border: '3px solid #8b4513',
              padding: 16,
            }}>
              {INGREDIENTS.map(ing => {
                const isSelected = selectedIngredients.includes(ing.id);
                const isRequired = requiredIngredients.includes(ing.id);
                const isWrong = isSelected && !isRequired;
                
                return (
                  <div
                    key={ing.id}
                    onClick={() => handleIngredientClick(ing.id)}
                    style={{
                      background: isWrong ? '#ffebee' : isSelected ? '#e8f5e9' : 'white',
                      border: `3px solid ${isWrong ? '#c62828' : isSelected ? '#4caf50' : '#333'}`,
                      padding: 10,
                      textAlign: 'center',
                      cursor: 'pointer',
                      position: 'relative',
                      boxShadow: '3px 3px 0 rgba(0,0,0,0.2)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <img 
                      src={ing.image}
                      alt={ing.name}
                      style={{ 
                        width: 40, 
                        height: 40, 
                        imageRendering: 'pixelated',
                        marginBottom: 4,
                      }}
                    />
                    <div style={{ fontSize: 10, fontWeight: 600 }}>{ing.name}</div>
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        background: isWrong ? '#c62828' : '#4caf50',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 600,
                        border: '2px solid #333',
                      }}>
                        {isWrong ? '✗' : '✓'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div style={{ background: '#e3f2fd', border: '2px solid #1565c0', padding: 12, marginTop: 16, fontSize: 13 }}>
              <strong>ℹ️ How it works:</strong> Click ingredients to select them. Green = correct, Red = wrong ingredient.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== ORDER QUEUE VIEW ==========
  return (
    <div>
      {/* Queue Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 24,
        padding: 16,
        background: 'var(--ltc-bg-secondary)',
        border: 'var(--ltc-border)',
      }}>
        <h2 style={{ 
          fontFamily: 'var(--ltc-font-display)', 
          color: 'var(--ltc-primary)',
          fontSize: 20,
          marginBottom: 4,
        }}>
          📋 Order Queue
        </h2>
        <p style={{ color: 'var(--ltc-text-muted)', fontSize: 14 }}>
          {pendingOrders.length} order{pendingOrders.length !== 1 ? 's' : ''} waiting
        </p>
        {needsNPCCustomers && (
          <p style={{ 
            color: 'var(--ltc-accent)', 
            fontSize: 12, 
            marginTop: 8,
            background: '#fff3e0',
            padding: '4px 12px',
            display: 'inline-block',
          }}>
            🤖 NPC Customers Active (no human customers joined)
          </p>
        )}
      </div>

      {pendingOrders.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 48,
          background: 'var(--ltc-bg-card)',
          border: 'var(--ltc-border)',
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>⏳</div>
          <p style={{ color: 'var(--ltc-text-muted)' }}>
            Waiting for customer orders...
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pendingOrders.map((order, idx) => {
            const menuItem = menu.find(m => m.id === order.menuItemId);
            if (!menuItem) return null;

            const waitTime = Math.floor((Date.now() - order.createdAt) / 1000);

            return (
              <div
                key={order.id}
                style={{
                  background: 'var(--ltc-bg-card)',
                  borderLeft: `5px solid ${idx === 0 ? '#ff6b6b' : '#4682b4'}`,
                  padding: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{
                  width: 64,
                  height: 64,
                  backgroundImage: `url(${getCustomerCharacterImage(order.customerId)})`,
                  backgroundSize: 'auto 100%',
                  backgroundPosition: 'left center',
                  backgroundRepeat: 'no-repeat',
                  imageRendering: 'pixelated',
                  flexShrink: 0,
                }}/>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>{getCustomerName(order.customerId)}</strong>
                    {idx === 0 && (
                      <span style={{ 
                        background: '#ff6b6b', 
                        color: 'white', 
                        padding: '2px 8px', 
                        fontSize: 11,
                        fontWeight: 600,
                      }}>
                        URGENT!
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                    ⏱️ Waiting: {formatTime(waitTime)}
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    marginBottom: 8,
                  }}>
                    <img 
                      src={menuItem.image}
                      alt={menuItem.name}
                      style={{ width: 32, height: 32, imageRendering: 'pixelated' }}
                    />
                    <span style={{ fontWeight: 500, color: 'var(--ltc-primary)' }}>
                      {menuItem.name}
                    </span>
                  </div>
                  <span style={{
                    background: '#fff3e0',
                    color: getComplexityColor(menuItem.complexity),
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 500,
                  }}>
                    {menuItem.complexity} ({menuItem.prepTime}min)
                  </span>
                </div>
                
                <button
                  onClick={() => handleClaimOrder(order.id)}
                  style={{
                    background: 'var(--ltc-accent)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  CLAIM ORDER
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
